import {
  all,
  put,
  call,
  fork,
  takeLatest,
  delay,
  select,
  race,
  take,
  cancelled
} from 'redux-saga/effects'
import { AnyAction } from 'redux'
import { NavigateFunction } from 'react-router-dom'

import { applicationApi } from 'services/application'
import { processApi } from 'services/process'
import { interfaceManagerApi } from 'services/interfaceManager'
import { securityApi } from 'services/security'
import { versionApi } from 'services/version'

import { UserInstanceData } from 'common/types/instance'
import {
  AppInstanceInfo,
  isOpenAppAsyncAccepted,
  OpenAppResponse,
  OpenAppStatus
} from 'common/types/application'
import { enqueue } from 'common/notifier'

import closeAction from 'common/notifier/CloseBtnEnq'
import { INSTANCE_ID } from 'common/api/storageConsts'

import { ApplicationState } from '..'

import { enqueueNotification } from '../notifier'
import { reloadInstances, resetAppUserInfo, setUserInstanceData } from '../user'
import { setForceStopPhase, setProgress, startWSServer, stopWSServer } from '../socket/actions'
import { SocketOpenAppTaskResultMessage } from '../socket/constants'
import { SocketActionTypes } from '../socket/types'

import { HomeItem } from 'common/types/home'
import {
  cancelCodeNode,
  navigateDiagram,
  removeNodeFromUnsaved,
  selectNode,
  setSelectedCodeNode
} from 'store/model'
import { modelApi } from 'services/model'
import { AppActionTypes } from './types'
import {
  addBroadcastChannel,
  openApp,
  removeBroadcastChannel,
  setAppConsolidationDifferences,
  setAppSavedFromOpenApp,
  setCancelOpenAppSuccess,
  setCancelingOpenApp,
  setHasActiveProcess,
  setClosingApp,
  setOpenAppAfterSave,
  showCreateAppDialog
} from '.'
import {
  setOpenApp,
  resetAppInfo,
  setSavingApp,
  setAppInfo,
  setChangingAppVersion,
  setCreatingAppVersion,
  setAssetsRecoveryDialog
} from './actions'
import {
  setChangeDefaultInterface,
  setInterfaceOpenedFromManager,
  setInterfaceTabs
} from 'store/interface/actions'
import { Interface, ListInterfaceResponse } from 'pages/interfaces/types'
import { consolidationAppApi } from 'services/consolidationApp'
import { GetDifferenceResponse } from 'pages/appConsolidation/types'
import { getRandomKey } from 'common/helpers/getRandomKey'
import { clearAllSessions } from 'store/agent'

function* handleOpenApp({
  payload: {
    navigate,
    fullPath,
    openAsReadOnly,
    version,
    resources,
    forceNewInstance,
    save,
    saveMsg,
    interfaceId,
    redirectTo,
    attemptRecovery
  }
}: AnyAction) {
  try {
    if (save) {
      yield* handleSaveApp({
        payload: {
          confirmNodeDefinitions: false,
          successMsg: saveMsg,
          openApp: null,
          savedFromOpenApp: false
        }
      } as any)
    }
    const currentAppInfo: AppInstanceInfo | undefined = ((state: ApplicationState) =>
      state.app.info)(yield select())
    //show open app dialog
    yield put(setProgress({ show: true, value: 5, message: 'creating_environment' }))
    yield put(resetAppUserInfo())
    yield call(applicationApi.closeApp)
    // Wait for Redis to delete instance
    yield delay(2000)
    yield put(resetAppInfo())
    yield put(clearAllSessions())

    // Race between opening the app and a successful cancellation
    yield race({
      openResult: call(openAppAndRedirect, {
        fullPath,
        navigate,
        openAsReadOnly,
        version,
        currentAppInfo,
        resources,
        forceNewInstance,
        interfaceId,
        redirectTo,
        attemptRecovery
      }),
      cancel: take(AppActionTypes.CANCEL_OPEN_APP_SUCCESS)
    })
  } catch (error: any) {
    yield put(enqueue(error))
  } finally {
    // takeLatest cancels this task when a newer OPEN_APP is dispatched (e.g.
    // a double-click on "open version"). If that's why we're here, leave
    // the progress bar alone — the newer task owns it now. Closing it
    // anyway used to force-navigate back to Home mid-open, before the
    // surviving task's real open finished.
    const wasCancelled: boolean = yield cancelled()
    if (!wasCancelled) {
      yield delay(1000)
      yield put(setProgress({ show: false, value: 100 }))
    }
  }
}

function* handleOpenAppOnNewInstance({
  payload: { navigate, fullPath, openAsReadOnly, version, resources, interfaceId }
}: AnyAction) {
  try {
    //show open app dialog
    yield put(setProgress({ show: true, value: 5, message: 'creating_environment' }))
    const res: [boolean, UserInstanceData] = yield call(securityApi.createInstance, {})
    const [success, instance] = res
    if (success) {
      yield put(resetAppInfo())
      yield put(clearAllSessions())
      const userInstance = instance as UserInstanceData
      sessionStorage[INSTANCE_ID] = userInstance.id
      yield put(setUserInstanceData(userInstance))
      yield put(startWSServer(navigate))

      // Race between opening the app and a successful cancellation
      yield race({
        openResult: call(openAppAndRedirect, {
          fullPath,
          navigate,
          openAsReadOnly,
          version,
          resources,
          forceNewInstance: true,
          interfaceId
        }),
        cancel: take(AppActionTypes.CANCEL_OPEN_APP_SUCCESS)
      })
    }
  } catch (error: any) {
    yield put(enqueue(error))
  } finally {
    // See handleOpenApp's finally: skip closing the progress bar when
    // takeLatest cancelled this task in favor of a newer one.
    const wasCancelled: boolean = yield cancelled()
    if (!wasCancelled) {
      yield delay(1000)
      yield put(setProgress({ show: false, value: 100 }))
    }
  }
}

function* handleCreateVersionFromClosedApp({
  payload: { navigate, folder, dataToSend, openOnNewInstance }
}: AnyAction) {
  try {
    //show open app dialog
    yield put(
      setProgress({
        show: true,
        value: 5,
        message: dataToSend?.create_in_my_workspace
          ? 'copying_app_to_user_workspace'
          : 'creating_environment'
      })
    )
    yield put(resetAppUserInfo())
    if (openOnNewInstance) {
      const res: [boolean, UserInstanceData] = yield call(securityApi.createInstance, {})
      const [success, instance] = res
      if (success) {
        const userInstance = instance as UserInstanceData
        sessionStorage[INSTANCE_ID] = userInstance.id
        yield put(setUserInstanceData(userInstance))
        yield put(startWSServer(navigate))
      }
    } else {
      yield call(applicationApi.closeApp)
      // Wait for Redis to delete instance
      yield delay(2000)
    }
    yield put(resetAppInfo())
    yield put(clearAllSessions())
    yield put(setCreatingAppVersion(true))

    // Race between creating version and a successful cancellation
    yield race({
      createResult: call(function* () {
        const appInfo: AppInstanceInfo = yield call(
          applicationApi.createVersionFromClosedApp,
          folder,
          dataToSend
        )
        yield* redirectAfterOpenApp(appInfo, navigate)
        return appInfo
      }),
      cancel: take(AppActionTypes.CANCEL_OPEN_APP_SUCCESS)
    })
  } catch (error: any) {
    yield put(enqueue(error))
  } finally {
    // See handleOpenApp's finally: skip closing the progress bar when
    // takeLatest cancelled this task in favor of a newer one.
    const wasCancelled: boolean = yield cancelled()
    if (!wasCancelled) {
      yield delay(1000)
      yield put(setProgress({ show: false, value: 100 }))
      yield put(setCreatingAppVersion(false))
    }
  }
}

// The OPEN_APP_TASK_RESULT websocket message is only ever sent once. If the
// socket happens to be down at that exact moment (dropped and still
// reconnecting), the message is gone for good and there's nothing left to
// wake this saga up — the UI would be stuck on "opening" forever. This polls
// GET /apps/openStatus/ as a fallback, same idea as pollOperationStatus for
// file operations (see plannings/open_app_async.md).
function* pollOpenAppStatus(folder: string, version?: string) {
  const POLL_INTERVAL_MS = 5000 // 5 seconds
  const MAX_POLL_DURATION_MS = 20 * 60 * 1000 // 20 minutes
  const MAX_CONSECUTIVE_POLL_ERRORS = 6
  const startedAt = Date.now()
  let consecutivePollErrors = 0

  while (true) {
    yield delay(POLL_INTERVAL_MS)
    if (Date.now() - startedAt > MAX_POLL_DURATION_MS) {
      throw new Error('error_opening_app')
    }
    try {
      const status: OpenAppStatus = yield call(applicationApi.getOpenStatus, folder, version)
      consecutivePollErrors = 0
      if (status.status === 'done') {
        return status
      }
      // 'opening' or 'unknown' (not recorded yet) — keep polling
    } catch (error) {
      consecutivePollErrors += 1
      if (consecutivePollErrors >= MAX_CONSECUTIVE_POLL_ERRORS) {
        throw new Error('error_opening_app')
      }
    }
  }
}

// When open() had to create a new engine, the API responds immediately with
// {instanceId, status: 'opening'} instead of the full AppInstanceInfo (see
// plannings/open_app_async.md). The real info arrives later over the socket
// as an OPEN_APP_TASK_RESULT message once open_app_task finishes — delivered
// on this instance's own socket connection, same as OPENING_APP. Races that
// against polling in case the websocket message never arrives.
function* waitForOpenAppTaskResult(folder: string, version?: string) {
  const { wsResult, pollResult }: { wsResult?: AnyAction; pollResult?: OpenAppStatus } = yield race(
    {
      wsResult: take(SocketActionTypes.OPEN_APP_TASK_RESULT),
      pollResult: call(pollOpenAppStatus, folder, version)
    }
  )

  let success = false
  let appInfo: AppInstanceInfo | undefined
  let error: string | undefined
  let logDetails: string | undefined

  if (wsResult) {
    const message: SocketOpenAppTaskResultMessage = wsResult.payload
    success = message.success
    appInfo = message.appInfo
    error = message.error
    logDetails = message.logDetails
  } else if (pollResult && pollResult.status === 'done') {
    success = pollResult.success
    if (pollResult.success) {
      appInfo = pollResult.appInfo
    } else {
      error = pollResult.error
      logDetails = pollResult.logDetails
    }
  }

  if (!success || !appInfo) {
    // Same detail level as the sync path (common/notifier/index.ts), which
    // only ever surfaces additional_details.log_details to the console, not
    // in the toast itself.
    if (logDetails) {
      console.error(`log_details:\n\n${logDetails}`)
    }
    throw new Error(error || 'error_opening_app')
  }
  return appInfo
}

function* openAppAndRedirect({
  fullPath,
  navigate,
  openAsReadOnly,
  version,
  currentAppInfo,
  resources,
  forceNewInstance,
  interfaceId,
  redirectTo,
  attemptRecovery
}: {
  fullPath: any
  navigate: NavigateFunction
  openAsReadOnly?: boolean
  version?: string
  currentAppInfo?: AppInstanceInfo
  resources?: string
  forceNewInstance?: boolean
  interfaceId?: string
  redirectTo?: string
  attemptRecovery?: boolean
}) {
  const openResult: OpenAppResponse = yield call(
    applicationApi.openApp,
    fullPath,
    openAsReadOnly,
    version,
    resources,
    forceNewInstance,
    attemptRecovery
  )
  const appInfo: AppInstanceInfo = isOpenAppAsyncAccepted(openResult)
    ? yield* waitForOpenAppTaskResult(fullPath, version)
    : openResult
  const channel: BroadcastChannel | undefined = ((state: ApplicationState) =>
    state.app.broadcastChannel)(yield select())
  if (currentAppInfo) {
    if (channel) {
      channel.postMessage(appInfo)
    }
  }
  yield put(
    addBroadcastChannel(
      `app-info-${appInfo.id}-${appInfo.name}-${appInfo.version}-${appInfo.engineParams}`
    )
  )
  yield* redirectAfterOpenApp(appInfo, navigate, interfaceId, redirectTo)
}

function* redirectAfterOpenApp(
  appInfo: AppInstanceInfo,
  navigate: NavigateFunction,
  interfaceId?: string,
  redirectTo?: string
) {
  yield put(setAppInfo(appInfo))
  if (appInfo.newInstanceId) {
    sessionStorage[INSTANCE_ID] = appInfo.newInstanceId
    if (appInfo.needNewWebSocket) {
      yield put(setProgress({ show: true, value: 90 }))
      const instanceData: UserInstanceData = yield call(securityApi.getInstance)
      const instanceWithNewData: UserInstanceData = {
        ...instanceData,
        id: appInfo.newInstanceId,
        applicationInfo: appInfo
      }
      yield put(setUserInstanceData(instanceWithNewData))
      yield put(startWSServer(navigate))
    }
  }
  if (appInfo.canEdit && appInfo.readonly && appInfo.canWriteInDir) {
    const keyReadOnly = getRandomKey()
    yield put(
      enqueueNotification({
        key: keyReadOnly,
        title: appInfo.readOnlyTitle,
        message: appInfo.readOnlyReason,
        options: {
          variant: 'info',
          persist: false,
          autoHideDuration: 3000,
          action: () => closeAction(keyReadOnly, 'info')
        }
      })
    )
  }
  if (appInfo.recoveredExistingInstance) {
    const keyRecoverInstance = getRandomKey()
    yield put(
      enqueueNotification({
        key: keyRecoverInstance,
        message: 'recovered_existing_instance',
        options: {
          variant: 'info',
          persist: false,
          autoHideDuration: 3000,
          action: () => closeAction(keyRecoverInstance, 'info')
        }
      })
    )
  }
  if (appInfo.assetsStatus === 'broken') {
    yield put(setAssetsRecoveryDialog({ versionPath: appInfo.assetsVersionPath }))
    yield put(setOpenApp(appInfo))
    // Assets (interfaces, business rules) are missing on disk: any defaultInterfaceId/
    // interfaceId comes from app.ppl, which survives independently of assets, so it no
    // longer resolves to a real interface. Don't navigate into it — that would try to
    // fetch/create a node for an interface that doesn't exist. Let the recovery dialog
    // own the screen until the user picks an option.
    navigate('')
    return
  }
  yield put(setOpenApp(appInfo))
  const defaultInterfaceId = appInfo.defaultInterfaceId || ''
  navigate('')
  if (redirectTo) {
    navigate(redirectTo)
  } else if (interfaceId && interfaceId !== defaultInterfaceId) {
    yield put(setInterfaceOpenedFromManager(interfaceId))
    const defaultInterface: { id: string; name: string } | undefined = yield call(
      applicationApi.getDefaultInterface
    )
    const defaultInterfaceTab = { id: defaultInterfaceId, name: defaultInterface?.name || '' }
    yield put(setInterfaceTabs([defaultInterfaceTab]))
    navigate(`/interfaces/${interfaceId}`)
  } else {
    navigate(`/interfaces${defaultInterfaceId && `/${defaultInterfaceId}`}`)
  }
}

function* handleCloseApp({ payload: { navigate, successMsg, save, saveMsg } }: AnyAction) {
  try {
    if (save) {
      yield* handleSaveApp({
        payload: {
          confirmNodeDefinitions: false,
          successMsg: saveMsg,
          openApp: null,
          savedFromOpenApp: false
        }
      } as any)
    }
    const res: boolean = yield call(applicationApi.closeApp)
    yield put(setClosingApp(true))
    yield put(resetAppInfo())
    yield put(clearAllSessions())
    yield put(reloadInstances(true))
    const channel: BroadcastChannel | undefined = ((state: ApplicationState) =>
      state.app.broadcastChannel)(yield select())
    if (channel) {
      // Send empty message so that other tabs with the same instance know they have to close the app too
      channel.postMessage(undefined)
    }
    yield put(removeBroadcastChannel())
    if (res) {
      navigate('/')
      const key = getRandomKey()
      yield put(
        enqueueNotification({
          key,
          message: successMsg,
          options: {
            variant: 'success',
            persist: false,
            autoHideDuration: 1500,
            action: () => closeAction(key, 'success')
          }
        })
      )
    }
    yield put(setClosingApp(undefined))
    // Create a new instance to avoid mixing existing instance ids
    yield* handleCreateNewInstance(navigate)
  } catch (error: any) {
    yield put(enqueue(error))
  }
}

function* handleCheckHasActiveProcess() {
  try {
    // Called with its own receiver: this api's methods are not bound, and
    // `call(api.method)` would run them with no `this` at all.
    const hasActiveProcess: boolean = yield call([processApi, processApi.hasActiveProcess])
    yield put(setHasActiveProcess(hasActiveProcess))
  } catch (error) {
    // Quiet for the user — this only decides whether an entry point is
    // offered, and it must not raise an error over whatever they are doing —
    // but never silent: a check that always fails looks exactly like "you are
    // in no process", which is a lie that hides the whole board.
    console.error('Could not check whether the user is in an active process:', error)
  }
}

function* handleCancelOpenApp({ payload: { navigate } }: AnyAction) {
  try {
    yield put(setCancelingOpenApp(true))
    const [success, instance]: [true, UserInstanceData] = yield call(applicationApi.cancelOpenApp)
    if (success) {
      yield put(resetAppInfo())
      yield put(clearAllSessions())
      yield put(reloadInstances(true))
      const userInstance = instance as UserInstanceData
      sessionStorage[INSTANCE_ID] = userInstance.id
      yield put(setUserInstanceData(userInstance))
      const channel: BroadcastChannel | undefined = ((state: ApplicationState) =>
        state.app.broadcastChannel)(yield select())
      if (channel) {
        // Send empty message so that other tabs with the same instance know they have to close the app too
        channel.postMessage(undefined)
      }
      yield put(removeBroadcastChannel())
      // Notify racing sagas that cancellation succeeded before starting the new WebSocket
      yield put(setCancelOpenAppSuccess())
      yield put(startWSServer(navigate))
      navigate('/')
    } else {
      navigate('/auth/')
    }
    yield put(setCancelingOpenApp(undefined))
  } catch (error: any) {
    yield put(setCancelingOpenApp(undefined))
    yield put(enqueue(error))
  }
}

function* handleCloseAppFromOtherTab({ payload: { navigate, message } }: AnyAction) {
  // This is used when the broadcastChannel receives a message to close the app because
  // it was closed explicitly in another tab. We must clean the application info and create a new instance
  try {
    // Delete app info and redirect to home
    yield put(resetAppInfo())
    yield put(reloadInstances(true))
    yield put(removeBroadcastChannel())
    navigate('/')
    // Create a new instance to avoid mixing existing instance ids
    yield* handleCreateNewInstance(navigate)
    yield put(
      enqueueNotification({
        message,
        options: {
          variant: 'info',
          persist: true
        }
      })
    )
  } catch (error: any) {
    yield put(enqueue(error))
  }
}

export function* confirmNodeDefinitionsBeforeSaving(confirmNodeDefinitions: boolean) {
  try {
    const unconfirmedNodes = ((state: ApplicationState) =>
      state.app.model.diagram?.unconfirmedNodes)(yield select())
    if (confirmNodeDefinitions) {
      if (unconfirmedNodes && unconfirmedNodes.length > 0) {
        for (const nodeToSave of unconfirmedNodes) {
          const properties: { name: string; value: any }[] = [
            { name: 'definition', value: nodeToSave.definition }
          ]
          const res: boolean = yield call(
            modelApi.setNodeProperties,
            nodeToSave.identifier,
            properties
          )
          if (res) {
            yield put(removeNodeFromUnsaved(nodeToSave.identifier))
          }
        }
      }
    } else {
      if (unconfirmedNodes) {
        for (const nodeToSave of unconfirmedNodes) {
          yield put(removeNodeFromUnsaved(nodeToSave.identifier))
        }
      }
    }
  } catch (error: any) {
    yield put(enqueue(error))
  }
}

export function* handleSaveApp({
  payload: { confirmNodeDefinitions, successMsg, openApp, savedFromOpenApp }
}: AnyAction) {
  try {
    yield* confirmNodeDefinitionsBeforeSaving(confirmNodeDefinitions)
    const unconfirmedNodesAfterIteration = ((state: ApplicationState) =>
      state.app.model.diagram?.unconfirmedNodes)(yield select())
    if (!unconfirmedNodesAfterIteration || unconfirmedNodesAfterIteration?.length === 0) {
      yield put(setSavingApp(true))
      const appInfo: AppInstanceInfo = yield call(applicationApi.saveApp)
      if (!confirmNodeDefinitions) {
        yield put(cancelCodeNode())
      }
      yield put(setAppInfo(appInfo))
      yield put(
        enqueueNotification({
          message: successMsg,
          options: {
            variant: 'success',
            persist: false
          }
        })
      )
      if (openApp && openApp.open) {
        yield put(setOpenAppAfterSave(openApp))
      }
    }
  } catch (error: any) {
    yield put(enqueue(error))
  } finally {
    yield put(setSavingApp(false))
    const saving = ((state: ApplicationState) => state.app.saving)(yield select())
    if (savedFromOpenApp && !saving) {
      yield put(setAppSavedFromOpenApp(true))
    }
  }
}

function* handleSaveAppInMyWorkspaceOrMyTeam({
  payload: {
    confirmNodeDefinitions,
    successMsg,
    newPathMsg,
    folderName,
    versions,
    navigate,
    teamFolder
  }
}: AnyAction) {
  try {
    yield* confirmNodeDefinitionsBeforeSaving(confirmNodeDefinitions)
    const unconfirmedNodesAfterIteration = ((state: ApplicationState) =>
      state.app.model.diagram?.unconfirmedNodes)(yield select())
    if (!unconfirmedNodesAfterIteration || unconfirmedNodesAfterIteration?.length === 0) {
      yield put(setSavingApp(true))
      const appInfo: AppInstanceInfo = teamFolder
        ? yield call(applicationApi.saveAppInMyTeam, folderName, teamFolder, versions)
        : yield call(applicationApi.saveAppInMyWorkspace, folderName, versions)
      if (!confirmNodeDefinitions) {
        yield put(setSelectedCodeNode(undefined))
      }
      // Reset all app info
      yield put(resetAppInfo())
      const channel = ((state: ApplicationState) => state.app.broadcastChannel)(yield select())
      if (channel) {
        channel.postMessage(appInfo)
      }
      yield put(
        addBroadcastChannel(
          `app-info-${appInfo.id}-${appInfo.name}-${appInfo.version}-${appInfo.engineParams}`
        )
      )
      yield put(setAppInfo(appInfo))
      yield put(setOpenApp(appInfo))

      // Navigate to default interface
      const defaultInterfaceId = appInfo.defaultInterfaceId || ''
      navigate('')
      navigate(`/interfaces${defaultInterfaceId && `/${defaultInterfaceId}`}`)

      yield put(
        enqueueNotification({
          message: successMsg,
          options: {
            variant: 'success',
            persist: false
          }
        })
      )
      yield put(
        enqueueNotification({
          message: `${newPathMsg}${appInfo.uri}`,
          options: {
            variant: 'info',
            persist: true
          }
        })
      )
    }
  } catch (error: any) {
    yield put(enqueue(error))
  } finally {
    yield put(setSavingApp(false))
  }
}

function* handleCreateApp({
  payload: {
    name,
    template,
    onPublic,
    pythonVersion,
    navigate,
    currentTeamTab,
    commonTabName,
    currentAppFolder
  }
}: AnyAction) {
  try {
    const newApp: HomeItem = yield call(
      applicationApi.createApp,
      name,
      template,
      onPublic,
      pythonVersion,
      currentTeamTab,
      commonTabName,
      currentAppFolder
    )
    yield put(showCreateAppDialog(false))
    yield put(openApp({ navigate, fullPath: newApp.uri }))
  } catch (error: any) {
    yield put(enqueue(error))
  } finally {
    yield put(setSavingApp(false))
  }
}

function* handleReloadWithMyVenv({ payload: { message } }: AnyAction) {
  try {
    const newAppInstanceInfo: AppInstanceInfo = yield call(applicationApi.reloadWithMyVenv)
    yield put(setOpenApp(newAppInstanceInfo))
    const keyReadOnly = getRandomKey()
    yield put(
      enqueueNotification({
        key: keyReadOnly,
        message: message,
        options: {
          variant: 'success',
          persist: false,
          autoHideDuration: 3000,
          action: () => closeAction(keyReadOnly, 'success')
        }
      })
    )
  } catch (error: any) {
    yield put(enqueue(error))
  }
}

function* handleDownloadExample({ payload: { name, navigate } }: AnyAction) {
  try {
    const newApp: HomeItem = yield call(applicationApi.downloadExample, name)
    yield put(openApp({ navigate, fullPath: newApp.uri, openAsReadOnly: true }))
  } catch (error: any) {
    yield put(enqueue(error))
  } finally {
    yield put(setSavingApp(false))
  }
}

/** Moves the page to a version the session already switched to. */
export function* applySwitchedAppVersion(appInfo: AppInstanceInfo, navigate: NavigateFunction) {
  yield put(resetAppInfo())
  const channel = ((state: ApplicationState) => state.app.broadcastChannel)(yield select())
  if (channel) {
    channel.postMessage(appInfo)
  }
  yield put(
    addBroadcastChannel(
      `app-info-${appInfo.id}-${appInfo.name}-${appInfo.version}-${appInfo.engineParams}`
    )
  )
  yield put(setAppInfo(appInfo))
  if (appInfo.readonly && appInfo.canWriteInDir) {
    const key = getRandomKey()
    yield put(
      enqueueNotification({
        title: appInfo.readOnlyTitle,
        message: appInfo.readOnlyReason,
        options: {
          variant: 'info',
          persist: false,
          autoHideDuration: 3000,
          action: () => closeAction(key, 'info')
        }
      })
    )
  }
  yield put(setOpenApp(appInfo))
  const defaultInterfaceId = appInfo.defaultInterfaceId || ''
  navigate('')
  navigate(`/interfaces${defaultInterfaceId && `/${defaultInterfaceId}`}`)
}

function* handleChangeAppVersion({ payload: { version, navigate } }: AnyAction) {
  try {
    yield put(setChangingAppVersion(true))
    // Leave the interface view before switching versions: the opened interface may not exist in the
    // target version and the still mounted view would try to recreate its node ('Interface not found')
    if (window.location.pathname.startsWith('/interfaces/')) {
      navigate('/')
      // let React unmount the interface view before the version change kicks in
      yield delay(0)
    }
    const appInfo: AppInstanceInfo = yield call(versionApi.changeAppVersion, version)
    yield* applySwitchedAppVersion(appInfo, navigate)
  } catch (error: any) {
    yield put(enqueue(error))
  } finally {
    yield put(setChangingAppVersion(false))
  }
}

function* handleSaveAppVersionInAnotherApp({
  payload: {
    app_path,
    new_version_name,
    labels,
    set_as_default,
    version_description,
    technical_version,
    navigate
  }
}: AnyAction) {
  try {
    yield put(setCreatingAppVersion(true))
    // Save app version
    try {
      const appInfo: AppInstanceInfo = yield call(versionApi.saveAppVersionInAnotherApp, {
        app_path,
        new_version_name,
        labels,
        set_as_default,
        version_description,
        technical_version
      })
      yield put(resetAppInfo())
      const channel = ((state: ApplicationState) => state.app.broadcastChannel)(yield select())
      if (channel) {
        channel.postMessage(appInfo)
      }
      yield put(
        addBroadcastChannel(
          `app-info-${appInfo.id}-${appInfo.name}-${appInfo.version}-${appInfo.engineParams}`
        )
      )
      yield put(setAppInfo(appInfo))
    } catch (error: any) {
      throw error
    } finally {
      yield put(setCreatingAppVersion(false))
    }
    // Close app and open the destination app with the new version
    yield* handleOpenApp({
      payload: {
        navigate,
        fullPath: app_path,
        openAsReadOnly: false,
        version: new_version_name
      },
      type: AppActionTypes.OPEN_APP
    })
  } catch (error: any) {
    yield put(enqueue(error))
  }
}

function* handleSaveAppAsNewVersion({
  payload: {
    new_version_name,
    labels,
    set_as_default,
    version_description,
    technical_version,
    navigate,
    preventNavigate,
    getConsolidationDifferenceData
  }
}: AnyAction) {
  try {
    yield put(setChangingAppVersion(true))
    yield* confirmNodeDefinitionsBeforeSaving(true)
    const appInfo: AppInstanceInfo = yield call(versionApi.saveAppInNewVersion, {
      new_version_name,
      labels,
      set_as_default,
      version_description,
      technical_version
    })
    yield put(resetAppInfo())
    const channel = ((state: ApplicationState) => state.app.broadcastChannel)(yield select())
    if (channel) {
      channel.postMessage(appInfo)
    }
    yield put(
      addBroadcastChannel(
        `app-info-${appInfo.id}-${appInfo.name}-${appInfo.version}-${appInfo.engineParams}`
      )
    )
    yield put(setAppInfo(appInfo))
    if (appInfo.readonly && appInfo.canWriteInDir) {
      const key = getRandomKey()
      yield put(
        enqueueNotification({
          title: appInfo.readOnlyTitle,
          message: appInfo.readOnlyReason,
          options: {
            variant: 'info',
            persist: false,
            autoHideDuration: 3000,
            action: () => closeAction(key, 'info')
          }
        })
      )
    }
    yield put(setOpenApp(appInfo))
    if (!preventNavigate) {
      const defaultInterfaceId = appInfo.defaultInterfaceId || ''
      navigate('')
      navigate(`/interfaces${defaultInterfaceId && `/${defaultInterfaceId}`}`)
    } else if (getConsolidationDifferenceData) {
      const consolidationDifference: GetDifferenceResponse = yield call(
        consolidationAppApi.getConsolidationDifferences,
        getConsolidationDifferenceData
      )
      yield put(setAppConsolidationDifferences(consolidationDifference))
    }
  } catch (error: any) {
    yield put(enqueue(error))
  } finally {
    yield put(setChangingAppVersion(false))
  }
}

function* handleUpdateAppVersionProperties({
  payload: { version, newProperties, navigate, location, applicationLanguage }
}: AnyAction) {
  const previousDefaultInterfaceId = ((state: ApplicationState) =>
    state.app.info?.defaultInterfaceId)(yield select())
  try {
    const appInfo: AppInstanceInfo = yield call(
      versionApi.updateAppVersionProperties,
      version,
      newProperties,
      applicationLanguage
    )
    yield put(setAppInfo(appInfo))
    const defaultInterfaceId = appInfo.defaultInterfaceId
    if (defaultInterfaceId !== previousDefaultInterfaceId) {
      const tabs = ((state: ApplicationState) => state.app.interface.tabs)(yield select())
      const interfaces: ListInterfaceResponse<Interface> = yield call(
        interfaceManagerApi.listInterfaces
      )
      const defaultInterface = interfaces?.results?.find((item) => item.id === defaultInterfaceId)
      let newTabs = [...tabs].filter((tab) => tab.id !== defaultInterfaceId)
      if (defaultInterface) {
        newTabs.unshift({ id: defaultInterface.id, name: defaultInterface.name })
      }
      yield put(setChangeDefaultInterface(true))
      if (!location.pathname.includes('/interfaces/')) {
        yield put(setInterfaceTabs(newTabs))
      } else {
        if (defaultInterface) {
          yield put(setInterfaceTabs(newTabs))
          navigate('')
          navigate(`/interfaces/${defaultInterface.id}`)
        }
      }
    }
  } catch (error: any) {
    yield put(enqueue(error))
  }
}

function* handleCreateNewInstance(navigate: NavigateFunction) {
  const createInstanceRes: [boolean, UserInstanceData] = yield call(securityApi.createInstance, {})
  const [success, instance] = createInstanceRes
  if (success) {
    yield put(stopWSServer())
    const userInstance = instance as UserInstanceData
    sessionStorage[INSTANCE_ID] = userInstance.id
    yield put(setUserInstanceData(userInstance))
    yield put(startWSServer(navigate))
  }
}

function* handleForceStopAndReopen({
  payload: { navigate, fullPath, moduleId, selectedNodeId, withSave }
}: AnyAction) {
  try {
    const instanceData: UserInstanceData | undefined = yield select(
      (state: ApplicationState) => state.user.instanceData
    )
    const instanceId = instanceData?.id ?? sessionStorage.getItem(INSTANCE_ID) ?? undefined
    const openAsReadOnly: boolean | undefined = yield select(
      (state: ApplicationState) => state.app.info?.openAsReadOnly
    )

    // Step 1: best-effort save (short timeout — engine may reject due to stop flag)
    if (withSave) {
      try {
        yield race({
          save: call(applicationApi.saveApp),
          timeout: delay(5000)
        })
      } catch {
        // Engine rejected save (StopEvaluation or similar) — proceed with kill anyway
      }
    }

    // Step 2: force-destroy the current instance.
    // RULE: do NOT proceed until the old instance is confirmed deleted from Redis.
    // forceDestroyInstance blocks until all Redis keys are gone.
    yield put(setForceStopPhase('killing'))
    if (instanceId) {
      try {
        yield call([securityApi, securityApi.forceDestroyInstance], instanceId)
      } catch (err) {
        console.warn('[forceStop] forceDestroyInstance failed:', err)
      }
    } else {
      console.warn('[forceStop] no instanceId — skipping destroy')
    }

    // Step 3: create a new instance.
    // Clear the old instance-id from sessionStorage so the createInstance request
    // arrives without an instance-id header — otherwise Django tries to look up
    // the (now deleted) instance in Redis and throws.
    sessionStorage.removeItem(INSTANCE_ID)
    yield put(setForceStopPhase('creating'))
    yield put(resetAppInfo())
    yield put(clearAllSessions())
    const res: [boolean, UserInstanceData] = yield call(securityApi.createInstance, {})
    const [success, newInstance] = res

    if (!success) {
      yield put(setForceStopPhase(null))
      return
    }

    const userInstance = newInstance as UserInstanceData
    sessionStorage[INSTANCE_ID] = userInstance.id
    yield put(setUserInstanceData(userInstance))
    yield put(startWSServer(navigate))

    // Step 4: open the same app in the new instance
    yield put(setForceStopPhase('opening'))
    yield call(openAppAndRedirect, {
      fullPath,
      navigate,
      openAsReadOnly,
      forceNewInstance: true
    })

    // Step 5: go back to the code view, the module, and the previously selected node
    navigate('/code')
    if (moduleId) {
      yield delay(300)
      yield put(navigateDiagram(moduleId))
    }
    if (selectedNodeId) {
      yield delay(300)
      yield put(selectNode(selectedNodeId))
    }
  } catch (error: any) {
    yield put(enqueue(error))
  } finally {
    yield put(setForceStopPhase(null))
  }
}

function* watchGeneralActions() {
  yield all([
    takeLatest(AppActionTypes.OPEN_APP, handleOpenApp),
    takeLatest(AppActionTypes.OPEN_APP_ON_NEW_INSTANCE, handleOpenAppOnNewInstance),
    takeLatest(AppActionTypes.CREATE_VERSION_FROM_CLOSED_APP, handleCreateVersionFromClosedApp),
    takeLatest(AppActionTypes.CLOSE_APP, handleCloseApp),
    takeLatest(AppActionTypes.CLOSE_APP_FROM_OTHER_TAB, handleCloseAppFromOtherTab),
    takeLatest(AppActionTypes.SAVE_APP, handleSaveApp),
    takeLatest(AppActionTypes.SAVE_APP_IN_WORKSPACE_OR_TEAM, handleSaveAppInMyWorkspaceOrMyTeam),
    takeLatest(AppActionTypes.SAVE_APP_AS_NEW_VERSION, handleSaveAppAsNewVersion),
    takeLatest(AppActionTypes.CREATE_APP, handleCreateApp),
    takeLatest(AppActionTypes.RELOAD_WITH_MY_VENV, handleReloadWithMyVenv),
    takeLatest(AppActionTypes.DOWNLOAD_EXAMPLE, handleDownloadExample),
    takeLatest(AppActionTypes.CHANGE_APP_VERSION, handleChangeAppVersion),
    takeLatest(AppActionTypes.UPDATE_APP_VERSION_PROPERTIES, handleUpdateAppVersionProperties),
    takeLatest(AppActionTypes.SAVE_APP_VERSION_IN_ANOTHER_APP, handleSaveAppVersionInAnotherApp),
    takeLatest(AppActionTypes.CANCEL_OPEN_APP, handleCancelOpenApp),
    takeLatest(AppActionTypes.FORCE_STOP_AND_REOPEN, handleForceStopAndReopen),
    takeLatest(AppActionTypes.CHECK_HAS_ACTIVE_PROCESS, handleCheckHasActiveProcess)
  ])
}

function* appSaga() {
  yield all([fork(watchGeneralActions)])
}

export { appSaga }
